import type { AuthSession, AuthUser } from "aws-amplify/auth"
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from "aws-amplify/auth"
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react"
import { clearEmergencyContactsSkippedSession } from "@/lib/emergency-contact-onboarding"

export interface AuthState {
  user: AuthUser | null
  session: AuthSession | null
  userAttributes: Record<string, string | undefined> | null
  isLoading: boolean
  isAuthenticated: boolean
  signOut: () => Promise<void>
  checkAuthState: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<Omit<AuthState, "signOut" | "checkAuthState">>({
    user: null,
    session: null,
    userAttributes: null,
    isLoading: true,
    isAuthenticated: false,
  })

  const checkAuthState = useCallback(async () => {
    const localUserEmail = import.meta.env.VITE_LOCAL_USER_EMAIL
    if (localUserEmail) {
      setAuthState({
        user: { username: localUserEmail, userId: localUserEmail },
        session: null,
        userAttributes: { email: localUserEmail },
        isLoading: false,
        isAuthenticated: true,
      })
      return
    }

    try {
      const [user, session, userAttributes] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
        fetchUserAttributes(),
      ])

      setAuthState({
        user,
        session,
        userAttributes,
        isLoading: false,
        isAuthenticated: true,
      })
    } catch (error) {
      // UserUnAuthenticatedException is expected when user is not authenticated
      // Don't log this specific error as it's normal behavior
      const isAuthError =
        error instanceof Error &&
        (error.name === "UserUnAuthenticatedException" ||
          error.message.includes("User needs to be authenticated"))

      if (!isAuthError) {
        // biome-ignore lint/suspicious/noConsole: will replace with logger later
        console.error("Error checking auth state:", error)
      }

      setAuthState({
        user: null,
        session: null,
        userAttributes: null,
        isLoading: false,
        isAuthenticated: false,
      })
    }
  }, [])

  useEffect(() => {
    checkAuthState()

    // Listen for Cognito token changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      // Cognito tokens are typically stored with keys containing 'CognitoIdentityServiceProvider'
      if (event.key?.includes("CognitoIdentityServiceProvider")) {
        if (event.oldValue && !event.newValue) {
          // Token was removed (sign out occurred)
          checkAuthState()
        } else if (!event.oldValue && event.newValue) {
          // Token was added (sign in occurred)
          checkAuthState()
        } else if (event.oldValue !== event.newValue && event.newValue) {
          // Token was updated (refresh occurred)
          checkAuthState()
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [checkAuthState])

  const handleSignOut = async () => {
    try {
      clearEmergencyContactsSkippedSession()
      await signOut()
      setAuthState({
        user: null,
        session: null,
        userAttributes: null,
        isLoading: false,
        isAuthenticated: false,
      })
    } catch (error) {
      // Sign out failed - this is expected in some cases
      // biome-ignore lint/suspicious/noConsole: will replace with logger later
      console.error("Sign out failed:", error)
    }
  }

  const authStateWithSignOut: AuthState = {
    ...authState,
    signOut: handleSignOut,
    checkAuthState,
  }

  return <AuthContext.Provider value={authStateWithSignOut}>{children}</AuthContext.Provider>
}

export function useAuthContext(): AuthState {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return context
}
